import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import { useTranslation } from 'react-i18next';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const CROP_SIZE = 200; // 200px diameter circle
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_WHEEL_STEP = 0.1; // 10%
const ZOOM_BUTTON_STEP = 0.25; // 25%

interface LogoCropModalProps {
  isOpen: boolean;
  imageFile: File | null;
  onClose: () => void;
  onSave: (croppedFile: File) => void;
  title?: string;
  instructions?: string;
}

export function LogoCropModal({
  isOpen,
  imageFile,
  onClose,
  onSave,
  title,
  instructions,
}: LogoCropModalProps) {
  const { t } = useTranslation(['pages.settings', 'pages.projectDetails']);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [minZoom, setMinZoom] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load image when file changes
  useEffect(() => {
    if (!imageFile) {
      setImageSrc(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setImageSrc(objectUrl);

    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.width, height: img.height });
      
      // Calculate minimum zoom to ensure circle is always filled
      const calculatedMinZoom = Math.max(
        CROP_SIZE / img.width,
        CROP_SIZE / img.height
      );
      setMinZoom(Math.max(calculatedMinZoom, MIN_ZOOM));
      
      // Set initial zoom to minimum
      setZoom(calculatedMinZoom);
      
      // Center the image initially
      setPanOffset({ x: 0, y: 0 });
    };
    img.src = objectUrl;

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen]);

  // Constrain pan to keep crop area filled
  const constrainPan = useCallback((newZoom: number, newPan: { x: number; y: number }) => {
    if (!imageSize.width || !imageSize.height) return newPan;

    const scaledWidth = imageSize.width * newZoom;
    const scaledHeight = imageSize.height * newZoom;

    // Calculate bounds to prevent empty space in circle
    const minX = Math.min(0, CROP_SIZE - scaledWidth);
    const maxX = Math.max(0, scaledWidth - CROP_SIZE);
    const minY = Math.min(0, CROP_SIZE - scaledHeight);
    const maxY = Math.max(0, scaledHeight - CROP_SIZE);

    return {
      x: Math.max(minX, Math.min(maxX, newPan.x)),
      y: Math.max(minY, Math.min(maxY, newPan.y)),
    };
  }, [imageSize]);

  // Handle zoom with constraints
  const handleZoom = useCallback((delta: number) => {
    setZoom((prevZoom) => {
      const newZoom = Math.max(minZoom, Math.min(MAX_ZOOM, prevZoom + delta));
      
      // Constrain pan after zoom change
      const constrainedPan = constrainPan(newZoom, panOffset);
      setPanOffset(constrainedPan);
      
      return newZoom;
    });
  }, [minZoom, panOffset, constrainPan]);

  // Mouse wheel zoom - use ref to add non-passive event listener
  // Zoom around center of crop circle (like buttons), not mouse cursor
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -ZOOM_WHEEL_STEP : ZOOM_WHEEL_STEP;
      handleZoom(delta);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleZoom]);

  // Zoom in button
  const handleZoomIn = () => {
    handleZoom(ZOOM_BUTTON_STEP);
  };

  // Zoom out button
  const handleZoomOut = () => {
    handleZoom(-ZOOM_BUTTON_STEP);
  };

  // Reset zoom and pan
  const handleReset = () => {
    setZoom(minZoom);
    setPanOffset({ x: 0, y: 0 });
  };

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left mouse button
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - panOffset.x,
      y: e.clientY - panOffset.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const newPan = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };
    
    const constrainedPan = constrainPan(zoom, newPan);
    setPanOffset(constrainedPan);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - panOffset.x,
      y: touch.clientY - panOffset.y,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || e.touches.length !== 1) return;
    e.preventDefault();
    
    const touch = e.touches[0];
    const newPan = {
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    };
    
    const constrainedPan = constrainPan(zoom, newPan);
    setPanOffset(constrainedPan);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Crop and save
  const handleSave = async () => {
    if (!imageRef.current || !canvasRef.current || !imageSrc) return;

    setIsProcessing(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Set canvas size to crop size
      canvas.width = CROP_SIZE;
      canvas.height = CROP_SIZE;

      // Clear canvas with transparent background
      ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);

      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, 2 * Math.PI);
      ctx.clip();

      // Calculate source rectangle from image
      const imageWidth = imageRef.current.naturalWidth;
      const imageHeight = imageRef.current.naturalHeight;
      
      const cropRadiusInImage = CROP_SIZE / (2 * zoom);
      const sourceX = (imageWidth / 2) - (panOffset.x / zoom) - cropRadiusInImage;
      const sourceY = (imageHeight / 2) - (panOffset.y / zoom) - cropRadiusInImage;
      const sourceWidth = CROP_SIZE / zoom;
      const sourceHeight = CROP_SIZE / zoom;

      // Draw image at current zoom/pan position
      ctx.drawImage(
        imageRef.current,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        CROP_SIZE,
        CROP_SIZE
      );

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          setIsProcessing(false);
          return;
        }

        // Create File from blob
        const croppedFile = new File(
          [blob],
          imageFile?.name || 'logo.png',
          { type: 'image/png' }
        );

        setIsProcessing(false);
        onSave(croppedFile);
      }, 'image/png');
    } catch (error) {
      console.error('Error cropping image:', error);
      setIsProcessing(false);
    }
  };

  if (!isOpen || !imageSrc) return null;

  const modalTitle = title || t('pages.settings:sections.profile.cropModal.title') || 'Crop Logo';
  const modalInstructions = instructions || t('pages.settings:sections.profile.cropModal.instructions') || 'Drag to position and use zoom controls to adjust your logo. The circular area will be your logo.';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[600px] max-h-[600px] p-0" hideClose>
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <DialogTitle>{modalTitle}</DialogTitle>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={onClose}
                disabled={isProcessing}
                size={30}
                title={t('pages.settings:sections.profile.cropModal.cancel')}
              />
              <IconButtonSave
                onClick={() => {
                  void handleSave();
                }}
                disabled={isProcessing}
                size={30}
                title={isProcessing
                  ? t('pages.settings:sections.profile.photoUploading')
                  : t('pages.settings:sections.profile.cropModal.save')}
              />
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pb-4">
          <p className="text-sm text-slate-600 mb-4">
            {modalInstructions}
          </p>

          {/* Image container with crop overlay */}
          <div
            ref={containerRef}
            className="relative w-full h-[400px] bg-slate-100 rounded-lg overflow-hidden cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ userSelect: 'none' }}
          >
            {/* Image - rendered first so it's behind the overlay */}
            <div
              className="absolute top-1/2 left-1/2 z-0"
              style={{
                transform: `translate(-50%, -50%) translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                transformOrigin: 'center center',
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Crop preview"
                className="block"
                draggable={false}
                style={{
                  maxWidth: 'none',
                  maxHeight: 'none',
                }}
              />
            </div>

            {/* Dimmed overlay outside crop area */}
            <div className="absolute inset-0 pointer-events-none z-10">
              <svg className="w-full h-full">
                <defs>
                  <mask id="logo-crop-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <circle
                      cx="50%"
                      cy="50%"
                      r={CROP_SIZE / 2}
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0, 0, 0, 0.5)"
                  mask="url(#logo-crop-mask)"
                />
              </svg>
            </div>

            {/* Crop circle border - on top */}
            <div
              className="absolute top-1/2 left-1/2 pointer-events-none z-20"
              style={{
                transform: 'translate(-50%, -50%)',
                width: CROP_SIZE,
                height: CROP_SIZE,
                border: '2px solid white',
                borderRadius: '50%',
                boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.2)',
              }}
            />
          </div>

          {/* Hidden canvas for cropping */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Controls */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleZoomOut}
                disabled={zoom <= minZoom || isProcessing}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM || isProcessing}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isProcessing}
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                {t('pages.settings:sections.profile.cropModal.reset')}
              </Button>
            </div>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}

